import { Button } from '@consta/uikit/Button'
import { Checkbox } from '@consta/uikit/Checkbox'
import type { ChoiceGroupPropOnChange } from '@consta/uikit/ChoiceGroup'
import { ChoiceGroup } from '@consta/uikit/ChoiceGroup'
import { Combobox } from '@consta/uikit/Combobox'
import { Layout } from '@consta/uikit/Layout'
import { Modal } from '@consta/uikit/Modal'
import { Select } from '@consta/uikit/Select'
import { Text } from '@consta/uikit/Text'
import type { TextFieldPropOnChange } from '@consta/uikit/TextField'
import { TextField } from '@consta/uikit/TextField'
import { HORIZON, SEISMIC_SURVEY } from '@core/core/constants'
import { getFileName } from '@core/core/helpers'
import type { CubeBriefInfo, DomainType, Survey2dInfo } from '@core/core/types'
import type { Nullable } from 'common/types'
import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { SurveyChoiceGroupItems, SurveyType } from '../types'
import css from './AddingFilesModal.css'
import { AddingFilesModalCoordinatesFields } from './AddingFilesModalCoordinatesFields'
import { AddingFilesModalFooter as ModalFooter } from './AddingFilesModalFooter'
import { AddingFilesModalHeader as ModalHeader } from './AddingFilesModalHeader'
import {
  coordinates,
  CoordinateSelectionOption,
  coordinateSelectionRules,
  coordsTemplates,
  DOMAIN_TIME_ID,
  itemsSurveyGroup,
  surveyChoiceGroupItems,
} from './constants'
import type {
  AddingFilesModalPropsType as PropsType,
  CoordsTemplate,
  FormDataType,
  SurveyGroupType,
} from './types'

export const AddingFilesModal: FC<PropsType> = ({
  fileName,
  survey2DItems,
  survey3DItems,
  domainItems,
  objectName,
  initialSurveyGroup,
  isViewObjectName = true,
  isViewDomains = true,
  isViewAxisZDirection = false,
  isModalOpen,
  isOneFileDownload,
  treeObject,
  uploadingType,
  onCancel,
  onSave,
  onSaveForAll,
}) => {
  const [formData, setFormData] = useState<FormDataType>()
  const [valueSurveyGroup, setValueSurveyGroup] = useState<SurveyGroupType>(SurveyType.TwoD)
  const [isShowingCoordinateTemplates, setIsShowingCoordinateTemplates] = useState(false)
  const initSurveyChoiceGroup =
    surveyChoiceGroupItems[treeObject?.elementType === 'survey2d' ? 1 : 0]
  const [surveyChoiceGroup, setSurveyChoiceGroup] = useState(initSurveyChoiceGroup)

  const isDisabledObjectName = Boolean(objectName)
  const isSurvey = uploadingType === SEISMIC_SURVEY
  const isSurvey2D = valueSurveyGroup === SurveyType.TwoD
  const isSurvey3D = valueSurveyGroup === SurveyType.ThreeD
  const isSurveyItems = survey2DItems && survey3DItems
  const isNew2DSurvey = surveyChoiceGroup.name === SurveyChoiceGroupItems.New
  const isExistent2DSurvey = surveyChoiceGroup.name === SurveyChoiceGroupItems.Existent
  const isDisabledOnSaveBtn =
    (isSurveyItems && !(formData?.survey2D ?? formData?.survey3D)) ||
    (isViewDomains && !formData?.domain) ||
    (isViewObjectName && !formData?.objectName && isNew2DSurvey) ||
    (!formData?.survey2D && !isNew2DSurvey) ||
    (isSurvey2D && isShowingCoordinateTemplates && !formData?.coordsTemplates?.xOffset) ||
    (isSurvey2D && isShowingCoordinateTemplates && !formData?.coordsTemplates?.yOffset)
  const initSurvey2D = !isNew2DSurvey
    ? survey2DItems?.filter(i => i.id === treeObject?.projectEntityUid)[0]
    : undefined

  useEffect(() => {
    setSurveyChoiceGroup(initSurveyChoiceGroup)
    setFormData({
      objectName: getFileName(objectName, fileName),
      domain: formData?.domain ?? domainItems.find(i => i.id === DOMAIN_TIME_ID),
      areZValuesNegative: formData?.areZValuesNegative ?? false,
      coordinateSelectionRule: formData?.coordinateSelectionRule ?? coordinateSelectionRules[0],
      survey2D: initSurvey2D,
    })
  }, [fileName, domainItems])

  useEffect(() => {
    setFormData(formData => ({
      ...formData,
      survey2D: initSurvey2D,
    }))
  }, [treeObject, survey2DItems, surveyChoiceGroup])

  useEffect(() => {
    setIsShowingCoordinateTemplates(
      formData?.coordinateSelectionRule === CoordinateSelectionOption.Manual,
    )
  }, [formData?.coordinateSelectionRule])

  const onChange2DSurveyChoiceGroup = (value: { name: SurveyChoiceGroupItems }) => {
    setSurveyChoiceGroup(value)
  }

  const onChangeField: TextFieldPropOnChange = value => {
    setFormData(prev => ({
      ...prev,
      objectName: value ?? '',
    }))
  }

  const onChangeCoordinateField = (value: CoordinateSelectionOption) => {
    setFormData(prev => ({
      ...prev,
      coordinateSelectionRule: value,
    }))
  }

  const onChangeDomainField: ChoiceGroupPropOnChange<DomainType, false> = value => {
    setFormData(prev => ({
      ...prev,
      domain: value,
    }))
  }

  const onChangeZDirection = () => {
    setFormData(prev => ({
      ...prev,
      objectName: prev?.objectName,
      areZValuesNegative: !prev?.areZValuesNegative,
    }))
  }

  const onChangeSurvey2D = (value: Nullable<Survey2dInfo>) => {
    setFormData(prev => ({
      ...prev,
      survey2D: value ?? undefined,
      survey3D: undefined,
      objectName: prev?.objectName,
      domain: value?.domain ? domainItems.find(item => item.id === value.domain) : prev?.domain,
    }))
  }

  const onChangeSurvey3D = (value: Nullable<CubeBriefInfo>) => {
    setFormData(prev => ({
      ...prev,
      survey3D: value ?? undefined,
      survey2D: undefined,
      objectName: prev?.objectName,
      domain: value?.domain ? domainItems.find(item => item.id === value.domain) : prev?.domain,
    }))
  }

  const onChangeSurvey = (value: SurveyGroupType) => {
    setValueSurveyGroup(value)
  }

  const getCordTemplateLabel = (template: CoordsTemplate) => {
    return Object.values(template).join('-')
  }

  const onSelectTemplate = ({ xOffset, yOffset }: CoordsTemplate) => {
    setFormData(prev => ({
      ...prev,
      objectName: prev?.objectName,
      coordsTemplates: {
        xOffset,
        yOffset,
      },
    }))
  }

  const setHeaderCoordsValue = (coordsTemplates: CoordsTemplate) => {
    setFormData(prev => ({
      ...prev,
      objectName: prev?.objectName,
      coordsTemplates,
    }))
  }

  return (
    <Modal
      isOpen={isModalOpen}
      hasOverlay={true}
      rootClassName={css.rootContainer}
      className={css.window}
      onClickOutside={onCancel}
      onEsc={onCancel}>
      <ModalHeader fileName={fileName} />

      <Layout direction="column" className={css.selectSurvey}>
        {!!isSurvey2D && !!isSurvey && (
          <ChoiceGroup
            size="s"
            view="ghost"
            name="survey"
            className={css.marginBottom}
            value={surveyChoiceGroup}
            items={surveyChoiceGroupItems}
            multiple={false}
            getItemLabel={({ name }) => name}
            onChange={onChange2DSurveyChoiceGroup}
          />
        )}
        {!!isViewObjectName && !!isNew2DSurvey && (
          <TextField
            name="objectName"
            type="text"
            label="Название"
            labelPosition="top"
            size="s"
            className={css.marginBottom}
            disabled={isDisabledObjectName}
            value={formData?.objectName}
            required={!formData?.objectName}
            onChange={onChangeField}
          />
        )}
        {!isNew2DSurvey && !!isSurvey2D && !!survey2DItems && (
          <Combobox
            name="survey2D"
            placeholder="Начните вводить название съемки"
            label="Выбрать"
            className={css.marginBottom}
            size="s"
            dropdownClassName={css.selectDropdown}
            items={survey2DItems}
            value={formData?.survey2D}
            getItemKey={item => item.id}
            getItemLabel={item => item.name ?? ''}
            required={!formData?.survey2D}
            onChange={onChangeSurvey2D}
          />
        )}

        {!!isSurveyItems && (
          <>
            <Text view="secondary" size="s" weight="regular" className={css.labelChoiceGroup}>
              Тип
            </Text>
            <ChoiceGroup
              size="s"
              view="ghost"
              name="surveyGroup"
              className={css.marginBottom}
              value={valueSurveyGroup}
              items={itemsSurveyGroup}
              multiple={false}
              getItemLabel={(item: string) => item}
              onChange={onChangeSurvey}
            />

            {!!isSurvey2D && (
              <Select
                name="survey2D"
                placeholder="Выберите"
                label="Выберите съемку для загрузки"
                className={css.marginBottom}
                size="s"
                dropdownClassName={css.selectDropdown}
                items={survey2DItems}
                value={formData?.survey2D}
                getItemKey={item => item.id}
                getItemLabel={item => item.name ?? ''}
                onChange={onChangeSurvey2D}
              />
            )}

            {!!isSurvey3D && (
              <Select
                name="survey3D"
                placeholder="Выберите"
                label="Выберите съемку для загрузки"
                className={css.marginBottom}
                size="s"
                dropdownClassName={css.selectDropdown}
                value={formData?.survey3D}
                items={survey3DItems}
                getItemKey={item => item.id}
                getItemLabel={item => item.name}
                onChange={onChangeSurvey3D}
              />
            )}
          </>
        )}

        {!!isViewDomains && !isExistent2DSurvey && (
          <>
            <Text view="secondary" size="s" weight="regular" className={css.labelChoiceGroup}>
              Домен
            </Text>

            <ChoiceGroup
              size="s"
              view="ghost"
              name="domain"
              value={formData?.domain}
              items={domainItems}
              multiple={false}
              getItemLabel={({ name, id }) => `${id} (${name})`}
              getItemDisabled={({ id }) => id === 'Z' && uploadingType === HORIZON}
              onChange={onChangeDomainField}
            />
          </>
        )}

        {!!isSurvey2D && initialSurveyGroup === SurveyType.TwoD && (
          <>
            <Text view="secondary" size="s" weight="regular" className={css.coordinatesLabel}>
              Координаты
            </Text>
            <ChoiceGroup
              size="s"
              view="ghost"
              name="coordinateSelectionRule"
              value={formData?.coordinateSelectionRule}
              items={coordinateSelectionRules}
              multiple={false}
              truncate={true}
              getItemLabel={(label: string) => label}
              onChange={onChangeCoordinateField}
            />
            {!!isShowingCoordinateTemplates && (
              <>
                <div className={css.coordinatesContainer}>
                  <Text size="s" view="secondary" className={css.coordinatesText}>
                    Шаблоны координат
                  </Text>
                  {coordsTemplates.map((item, i) => (
                    <Button
                      key={i}
                      size="xs"
                      view="ghost"
                      label={getCordTemplateLabel(item)}
                      className={css.coordinatesBtn}
                      onClick={() => {
                        onSelectTemplate(item)
                      }}
                    />
                  ))}
                </div>
                <div className={css.coordinatesFields}>
                  {coordinates.map(item => (
                    <AddingFilesModalCoordinatesFields
                      key={item.name}
                      coordProps={item}
                      coordValue={formData?.coordsTemplates}
                      setHeaderCoordsValue={setHeaderCoordsValue}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {!!isViewAxisZDirection && (
          <div className={css.zValues}>
            <Checkbox
              name="areZValuesNegative"
              view="ghost"
              label="Отрицательные значения по оси Z"
              checked={!formData?.areZValuesNegative}
              onChange={onChangeZDirection}
            />
          </div>
        )}
      </Layout>

      <ModalFooter
        isViewSaveAllBtn={isOneFileDownload}
        isDisabledOnSaveBtn={isDisabledOnSaveBtn}
        onSave={() => {
          onSave(formData)
        }}
        onSaveForAll={() => {
          if (formData) {
            onSaveForAll(formData)
          }
        }}
        onCancel={onCancel}
      />
    </Modal>
  )
}
